import type { QueryClient } from "@tanstack/react-query";
import { getCoachingOrgId, getCoachingOrgIdHint, isPositiveIntegerOrgId } from "../services/coaching-org-context";
import { getCoachingServices, type CoachingServices } from "../services/coaching-service-provider";
import { coachingQueryKeys } from "./coaching-query-keys";

type FocusedListFamily = "resources.list" | "history.list";
type FocusedListOrgQuery = { orgId: string };

const focusedRouteFamilies: Record<string, FocusedListFamily> = {
  "/resources": "resources.list",
  "/history": "history.list"
};

export function focusedListOrgQuery(orgId: string | null = getCoachingOrgId()): FocusedListOrgQuery | undefined {
  return isPositiveIntegerOrgId(orgId) ? { orgId } : undefined;
}

export function focusedListQueryKey(family: FocusedListFamily, orgId: string | null = getCoachingOrgId()) {
  return coachingQueryKeys.list(family, focusedListOrgQuery(orgId) ?? {});
}

export function prefetchFocusedRouteList(
  queryClient: QueryClient,
  routePath: string,
  services: CoachingServices = getCoachingServices(),
  orgId: string | null = getCoachingOrgIdHint()
) {
  const family = focusedRouteFamilies[routePath];
  const query = focusedListOrgQuery(orgId);
  if (!family || !query) return false;

  void queryClient.prefetchQuery({
    queryKey: focusedListQueryKey(family, query.orgId),
    queryFn: () => readFocusedList(services, family, query),
    staleTime: 30_000
  });
  return true;
}

async function readFocusedList(services: CoachingServices, family: FocusedListFamily, query: FocusedListOrgQuery): Promise<unknown> {
  return family === "resources.list" ? services.resources.list(query) : services.history.list(query);
}
